<template>
  <v-container>
    <v-snackbar
      class="snackbar-top"
      v-model="notification.show"
      :top="true"
      :right="true"
      :color="notification.color"
    >
      {{ notification.message }}
      <v-btn
        fab small outlined
        @click="closeNotification"
      >
        <v-icon>clear</v-icon>
      </v-btn>
    </v-snackbar>

    <v-simple-table
      fixed-header
    >
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">{{$t('forms.name')}}</th>
            <th class="text-left">{{$t('forms.description')}}</th>
            <th class="text-right">{{$t('forms.price')}}</th>
            <th class="text-center">{{$t('forms.cycles.cycle')}}</th>
            <th class="text-center">
              <v-btn @click="addPlan" fab small color="primary" :disabled="loading">
                <v-icon>add</v-icon>
              </v-btn>
              <v-btn @click="loadPlans" fab small color="primary" class="ml-2" :disabled="loading">
                <v-icon>refresh</v-icon>
              </v-btn>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="plan in plans" :key="plan.id">
            <td>{{ plan.name }}</td>
            <td>{{ plan.description }}</td>
            <td class="text-right">$ {{ plan.price }}</td>
            <td class="text-center">{{ plan.cycle ? getCycleName(plan.cycle) : '' }}</td>
            <td class="text-center">
              <v-btn @click="editPlan(plan)" fab small :disabled="loading" color="blue accent-1" dark>
                <v-icon>edit</v-icon>
              </v-btn>
              <v-btn @click="removePlan(plan)" fab small :disabled="loading" class="ml-2" color="red accent-1" dark>
                <v-icon>delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <v-dialog width="400" v-model="dialog_form">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          {{current_plan.id ? $t('forms.edit-record', {name: $t('forms.plan')}) : $t('forms.create-record', {name: $t('forms.plan')})}}
        </v-card-title>
        <v-card-text>
          <v-alert type="error" dismissible v-if="error" class="mt-5">{{error}}</v-alert>
          <v-alert type="success" dismissible v-if="notice" class="mt-5">{{notice}}</v-alert>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  :label="$t('forms.name')"
                  type="text"
                  v-model="current_plan.name"
                  :rules="[v => !!v ||  this.$t('forms.is-required', {name: this.$t('forms.name')})]"
                  required
                ></v-text-field>

                <v-textarea
                  solo
                  :label="$t('forms.description')"
                  v-model="current_plan.description"
                  :rules="[v => !!v ||  this.$t('forms.is-required', {name: this.$t('forms.description')})]"
                  required
                ></v-textarea>

                <v-row>
                  <v-col cols="6">
                    <v-text-field
                    v-currency="currency_options"
                    outlined
                    prepend-inner-icon="attach_money"
                    :label="$t('forms.price')"
                    type="text"
                    v-model="current_plan.price"
                    :rules="[v => !!v ||  this.$t('forms.is-required', {name: this.$t('forms.price')})]"
                    required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-select
                    outlined
                    :label="$t('forms.cycles.cycle')"
                    v-model="current_plan.cycle"
                    :items="cycles"
                    item-text="name"
                    item-value="value"
                    required
                    ></v-select>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="savePlan" :loading="loading" :disabled="loading">
            {{$t('forms.save')}}
            <template v-slot:loader>
              <span>{{$t('forms.loading-save')}}</span>
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="dialog_remove" max-width="500">
        <v-card>
            <v-card-title class="headline">{{$t('forms.sure-remove', {name: current_plan.name})}}</v-card-title>
            <v-card-text>
                <p>{{$t('forms.sure-remove-description')}}</p>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="cancelRemove">
                    {{ $t('forms.no-remove') }}
                </v-btn>
                <v-btn color="red accent-2" dark @click="confirmRemovePlan">
                    {{ $t('forms.confirm-remove') }}
                </v-btn>
            </v-card-actions>
            <v-overlay
              :value="loading"
              >
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
            </v-overlay>
        </v-card>
   </v-dialog>

   <v-overlay :value="!loaded" opacity="1" color="white" light absolute>
     <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
   </v-overlay>
  </v-container>
</template>

<style lang="scss">
  .v-snack--top {
      top: 5em !important;
  }
</style>

<script>
import * as service from '@/store/module/plan'

export default {
  data() {
    return {
      plans: [],
      valid: false,
      loaded: false,
      loading: false,
      error: null,
      notice: null,
      dialog_form: false,
      dialog_remove: false,
      current_plan: null,
      notification: {
        color: 'gree',
        show: false,
        message: ''
      },
      cycles: [
        {value: 1, name: this.$t('forms.cycles.monthly')},
        {value: 3, name: this.$t('forms.cycles.quarterly')},
        {value: 6, name: this.$t('forms.cycles.biannual')},
        {value: 12, name: this.$t('forms.cycles.annual')}
      ],
      currency_options: {
        currency: null,
        precision: 0,
        valueAsInteger: true,
        allowNegative: false,
        distractionFree: {
          hideNegligibleDecimalDigits: false,
          hideCurrencySymbol: false,
          hideGroupingSymbol: false
        }
      }
    }
  },
  created() {
    this.resetForm()
    this.loadPlans()
  },
  methods: {
    showNotification(success, message) {
      this.notification.message = message
      this.notification.color = success ? 'green' : 'red'
      this.notification.show = true
    },
    closeNotification() {
      this.notification.show = false
      this.notification.message = ''
    },
    loadPlans() {
      this.loaded = false
      service.getDocumentsFromCollection('plans').then(result => {
        this.plans = result
        this.loaded = true
      })
    },
    resetForm() {
      this.current_plan = {
        id: null,
        name: '',
        description: '',
        price: 0,
        cycle: 1
      }

      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    openFormDialog() {
      this.dialog_form = true
    },
    closeFormDialog() {
      this.dialog_form = false
    },
    openRemoveDialog() {
      this.dialog_remove = true
    },
    closeRemoveDialog() {
      this.dialog_remove = false
    },
    removePlan(plan) {
      this.current_plan = plan
      this.openRemoveDialog()
    },
    cancelRemove() {
      this.closeRemoveDialog()
      this.resetForm()
    },
    confirmRemovePlan() {
      service.removePlan(this.current_plan.id)
        .then(() => {
          this.showNotification(true, this.$t("forms.data-removed"))
          this.cancelRemove()
          this.loadPlans()
        })
        .catch(() => {
          this.showNotification(true, this.$t("errors.remove"))
        })
    },
    getCycleName(cycle) {
      return this.cycles.find(c => c.value === cycle).name
    },
    addPlan() {
      this.resetForm()
      this.openFormDialog()
    },
    editPlan(plan) {
      this.loading = true
      this.resetForm()

      this.current_plan = {
        id: plan.id,
        name: plan.name,
        description: plan.description,
        price: plan.price,
        cycle: plan.cycle
      }
      this.openFormDialog()
      this.loading = false
    },
    savePlan() {
      if (this.$refs.form.validate()) {
        this.loading = true
        this.notice = null
        this.error = null

        let plan = {
          id: this.current_plan.id ? this.current_plan.id : null,
          name: this.current_plan.name,
          description: this.current_plan.description,
          price: this.current_plan.price,
          cycle: this.current_plan.cycle
        }

        let action_save = this.current_plan.id ? service.updatePlan : service.createPlan
        action_save(plan)
        .then(() => {
          // End save process
          this.showNotification(true, this.$t("forms.saved-record"))
          this.closeFormDialog()
          this.$refs.form.reset()
          this.loading = false
          this.loaded = false
          this.loadPlans()
        })
        .catch(error => {
          this.error = error.message
          this.loading = false
        })
      }
    }
  }
}
</script>
